import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcCafe } from './CalccafeContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './CustosCafe.css';

// Funções de formatação
const formatCurrencyForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
};

const CustosCafe = () => {
  const { dados, setDados } = useCalcCafe();
  const navigate = useNavigate();

  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [mostrarAjuda, setMostrarAjuda] = useState(false);
  const [mensagemErro, setMensagemErro] = useState("");

  useEffect(() => {
    if (!dados.custos) {
      setDados(prev => ({
        ...prev,
        custos: {
          custoManutencao: '', maoDeObraAnual: '', despesasFinanceiras: ''
        }
      }));
    }
    if (dados.resultadosCustos) {
      setMostrarResultados(true);
    }
  }, [dados.custos, dados.resultadosCustos, setDados]);

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({ ...prev, custos: { ...prev.custos, [name]: rawValue } }));
  };

  const handleCalcular = () => {
    // 1. Puxa dados das telas anteriores do contexto
    const producaoTotalSacas = (Number(dados.producao?.producaoTotal) || 0) / 100;
    const depreciacaoCafe = parseFloat(dados.resultadosCapital?.depreciacao) || 0;
    const rentabilidadeCapitalInvestidoCafe = parseFloat(dados.resultadosCapital?.rentabilidadeEsperadaCapital) || 0;

    let mensagem = "";
    if (producaoTotalSacas === 0) mensagem += " 'Produção Total' (da tela de Produção)";
    if (depreciacaoCafe === 0 || rentabilidadeCapitalInvestidoCafe === 0) mensagem += " e 'Resultados de Capital' (da tela de Capital)";
    
    if (mensagem) {
        setMensagemErro(`Para um cálculo completo, preencha os campos na(s) tela(s) anterior(es):${mensagem}.`);
    } else {
        setMensagemErro("");
    }

    // 2. Puxa dados dos inputs desta tela
    const c = dados.custos;
    const custoManutencao = (Number(c.custoManutencao) || 0) / 100;
    const maoDeObraAnual = (Number(c.maoDeObraAnual) || 0) / 100;
    const despesasFinanceiras = (Number(c.despesasFinanceiras) || 0) / 100;
    
    // 3. Executa os cálculos
    const custoOperacionalCafe = custoManutencao + maoDeObraAnual + despesasFinanceiras;
    const custoTotalCafe = custoOperacionalCafe + rentabilidadeCapitalInvestidoCafe + depreciacaoCafe;
    const custoOperacionalPorSaca = producaoTotalSacas ? custoOperacionalCafe / producaoTotalSacas : 0;
    const custoTotalPorSaca = producaoTotalSacas ? custoTotalCafe / producaoTotalSacas : 0;

    const resultados = {
      custoOperacionalCafe: custoOperacionalCafe.toFixed(2),
      custoTotalCafe: custoTotalCafe.toFixed(2),
      custoOperacionalPorSaca: custoOperacionalPorSaca.toFixed(2),
      custoTotalPorSaca: custoTotalPorSaca.toFixed(2),
    };

    setDados(prev => ({ ...prev, resultadosCustos: resultados }));
    setMostrarResultados(true);
  };

  const formatCurrencyResult = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  
  const handleDownloadPDF = () => {
    if (!dados.resultadosCustos) {
      alert("Calcule os resultados primeiro para gerar o PDF.");
      return;
    }
    const doc = new jsPDF();
    const c = dados.custos;
    const r = dados.resultadosCustos;
    const dataAnalise = dados.producao?.dataSelecionada;
    
    const primaryColor = [111, 78, 55];
    const darkColor = [62, 39, 35];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório de Custos - CalcCafé", 105, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (dataAnalise) {
        doc.text(`Análise para ${new Date(dataAnalise).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}`, 105, 20, { align: 'center' });
    }
    
    const dadosInseridosBody = [
        ['Custo de Manutenção', formatCurrencyResult((Number(c.custoManutencao)/100) || 0)],
        ['Mão-de-Obra Anual', formatCurrencyResult((Number(c.maoDeObraAnual)/100) || 0)],
        ['Despesas Financeiras', formatCurrencyResult((Number(c.despesasFinanceiras)/100) || 0)],
    ];
    autoTable(doc, {
        startY: 40,
        head: [['Dados Inseridos', 'Valor (R$)']],
        body: dadosInseridosBody,
        theme: 'striped',
        headStyles: { fillColor: darkColor },
    });

    const resultadosBody = [
        ['Custo Operacional', formatCurrencyResult(r.custoOperacionalCafe)],
        ['Custo Total', formatCurrencyResult(r.custoTotalCafe)],
        ['Custo Operacional por Saca', formatCurrencyResult(r.custoOperacionalPorSaca)],
        ['Custo Total por Saca', formatCurrencyResult(r.custoTotalPorSaca)],
    ];
    autoTable(doc, {
        head: [['Indicador de Custo', 'Resultado Calculado']],
        body: resultadosBody,
        theme: 'grid',
        headStyles: { fillColor: primaryColor },
    });
    
    doc.save("relatorio_custos_calccafe.pdf");
  };

  const handleLimparDados = () => {
    if(window.confirm("Tem certeza que deseja limpar os dados desta tela?")) {
        setDados(prev => {
            const { custos, resultadosCustos, ...resto } = prev;
            localStorage.setItem('calcCafeData', JSON.stringify(resto));
            return resto;
        });
        setMostrarResultados(false);
    }
  };

  return (
    <div className="cafe-custos-container">
      <div className="cafe-custos-card">
        <h1>Custos de Produção</h1>
        <p className="cafe-custos-subtitle">Dados para calcular o custo de produção e/ou manutenção.</p>

        <div className="cafe-custos-form-results-grid">
            <div className="cafe-custos-form">
                <div className="cafe-custos-input-group">
                    <label>Custo de Manutenção (R$)</label>
                    <input type="text" name="custoManutencao" placeholder="Ex: 25.000,00" value={formatCurrencyForDisplay(dados.custos?.custoManutencao)} onChange={handleCurrencyChange} />
                </div>
                <div className="cafe-custos-input-group">
                    <label>Mão-de-Obra Anual (R$)</label>
                    <input type="text" name="maoDeObraAnual" placeholder="Ex: 50.000,00" value={formatCurrencyForDisplay(dados.custos?.maoDeObraAnual)} onChange={handleCurrencyChange} />
                </div>
                <div className="cafe-custos-input-group">
                    <label>Despesas Financeiras (R$)</label>
                    <input type="text" name="despesasFinanceiras" placeholder="Ex: 2.000,00" value={formatCurrencyForDisplay(dados.custos?.despesasFinanceiras)} onChange={handleCurrencyChange} />
                </div>
                <button className="cafe-custos-button" onClick={handleCalcular}>Calcular</button>
                <button className="cafe-custos-help-button" onClick={() => setMostrarAjuda(true)}>O que incluir nos gastos?</button>
            </div>

            {mostrarResultados && (
              <div className="cafe-custos-results">
                <h2>Resultados</h2>
                <ul>
                  <li>Custo Operacional: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoOperacionalCafe)}</strong></li>
                  <li>Custo Total: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoTotalCafe)}</strong></li>
                  <li>Custo Operacional por Saca: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoOperacionalPorSaca)}</strong></li>
                  <li>Custo Total por Saca: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoTotalPorSaca)}</strong></li>
                </ul>
                <button className="cafe-custos-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
              </div>
            )}
        </div>
        
        {mensagemErro && <p className="cafe-custos-warning">{mensagemErro}</p>}

        <div className="cafe-custos-navigation">
            <button className="cafe-custos-button back" onClick={() => navigate('/calccafe/app/capitalCafe')}>Voltar para Capital</button>
            <button className="cafe-custos-button clean" onClick={handleLimparDados}>Limpar Dados</button>
            <button className="cafe-custos-button navigate" onClick={() => navigate('/calccafe/app/receitaCafe')}>Avançar para Receita</button>
        </div>
      </div>
      
      {mostrarAjuda && (
        <div className="ajuda-modal-backdrop" onClick={() => setMostrarAjuda(false)}>
            <div className="ajuda-modal-content" onClick={e => e.stopPropagation()}>
                <h2>Ajuda - Custos (Café)</h2>
                <p><strong>Custo de Manutenção:</strong> Inclui todos os gastos com insumos como fertilizantes, defensivos, herbicidas, etc.</p>
                <p><strong>Mão-de-Obra:</strong> Salários e encargos de todos os funcionários envolvidos na produção.</p>
                <p><strong>Despesas Financeiras:</strong> Juros de financiamentos, empréstimos ou outras despesas bancárias relacionadas à produção.</p>
                <button onClick={() => setMostrarAjuda(false)}>Fechar</button>
            </div>
        </div>
      )}
    </div>
  );
};

export default CustosCafe;

